import { GroupSubtypeEnum } from 'app/schemas/plan';
import { Text } from 'app/design/primitives/text';
import { Column } from 'app/design/primitives/box';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, type TextInput, View } from 'react-native';
import { Modal } from 'app/design/primitives/modal';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Button } from 'app/design/primitives/button';
import { useKeyboard } from '@react-native-community/hooks';
import { isFreeTrialPlanCode, useCreateOnePlan, useMyActivePlans } from 'app/network/plan';
import { type CreatePlanDTO } from 'app/types/plan';
import * as yup from 'yup';
import { useForm } from 'app/hooks/form';
import { log } from 'app/lib/monitoring';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useAtomValue, useSetAtom } from 'jotai';
import { pauseModalsAtom, presentB2BActivatedModalAtom, userInfoAtom } from 'app/atoms';
import { buildError } from './activation-modal.utils';

export { buildError } from './activation-modal.utils';

export default function ActivationModal({
  isOpenState,
}: Readonly<{
  isOpenState: [boolean, Dispatch<SetStateAction<boolean>>];
}>) {
  const { t } = useTranslation('my-plan');
  const keyboard = useKeyboard();
  const [isOpen, setIsOpen] = isOpenState;

  const schema: yup.ObjectSchema<CreatePlanDTO> = yup.object({
    code: yup
      .string()
      .trim()
      .required(t('modal.error.missingCode', { ns: 'my-plan' }))
      .defined(),
  });

  const activationCodeInputRef = useRef<TextInput>(null);

  const { control, handleSubmit, reset, formState } = useForm({
    schema,
  });
  const {
    mutate,
    error: createPlanError,
    isPending: isCreatingPlan,
  } = useCreateOnePlan({
    onSuccess: () => {
      setIsOpen(false);
      reset();
    },
  });

  const presentB2BActivatedModal = useSetAtom(presentB2BActivatedModalAtom);
  const pauseModals = useSetAtom(pauseModalsAtom);
  const onSubmit = handleSubmit(({ code }) => {
    try {
      pauseModals(true);
      mutate(
        { code },
        {
          onSuccess: () => {
            setIsOpen(false);
            presentB2BActivatedModal(true);
          },
          onSettled: () => pauseModals(false),
        },
      );
    } catch (err) {
      log(err);
      pauseModals(false);
    }
  });

  useEffect(() => {
    if (isOpen) {
      activationCodeInputRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <Modal
      columnProps={
        Platform.OS === 'ios'
          ? {
              justifyContent: keyboard.keyboardShown ? 'flex-end' : 'center',
            }
          : {}
      }
      onClose={() => setIsOpen(false)}
      open={isOpen}
      transparent
    >
      <Modal.Card>
        <Modal.Title>{t('modal.title')}</Modal.Title>

        <Column>
          <Text textAlign="center">{t('modal.firstParagraph')}</Text>
        </Column>

        <ControlledInput
          {...{ control }}
          autoCapitalize="characters"
          innerRef={activationCodeInputRef}
          label={t('modal.codeFieldLabel')}
          labelColor="text.primary"
          name="code"
          placeholder={t('modal.codeFieldPlaceholder')}
        />

        {createPlanError ? buildError(t, createPlanError) : null}

        <View>
          <Button
            loading={formState.isLoading || isCreatingPlan}
            marginBottom="spacing.general.x4"
            onPress={() => void onSubmit()}
            variant="primary"
          >
            {t('modal.primaryButtonText')}
          </Button>

          <Button onPress={() => setIsOpen(false)} variant="secondary">
            {t('modal.secondaryButtonText')}
          </Button>
        </View>
      </Modal.Card>
      {/* Added an empty view to push the Modal.Card up when the keyboard is shown */}
      {Platform.OS === 'ios' && (
        <View
          style={{
            marginBottom: keyboard.keyboardShown ? keyboard.keyboardHeight : undefined,
          }}
        />
      )}
    </Modal>
  );
}

export const useActivationModal = () => {
  const isActivationModalOpen = useState(false);

  const { data: userPlans } = useMyActivePlans();

  const activePlan = userPlans?.[0];

  const isExpired = activePlan?.expiresAt && new Date(activePlan.expiresAt).getTime() < new Date().getTime();

  const groupAllowMembersListing = activePlan?.code.group?.allowMembersListing;
  const groupSubtype = activePlan?.code.group?.subtype;
  const isMunicipalPlan =
    groupSubtype === GroupSubtypeEnum.municipal || groupSubtype === GroupSubtypeEnum.municipal_closed;

  const isOnFreeTrial = isFreeTrialPlanCode(activePlan?.code.code);
  const isB2C = isOnFreeTrial || (activePlan && !activePlan.code.group);

  const userInfo = useAtomValue(userInfoAtom);
  const justOneClub = isFeatureEnabled(FeatureFlag.justOneClub, userInfo?.featureFlags);

  return {
    isActivationModalOpen,
    openActivationModal: () => isActivationModalOpen[1](true),
    // Municipal members can replace their city plan with another activation code, including when their experience is limited to one club.
    showActivationButton: isMunicipalPlan || (!justOneClub && (isB2C || !groupAllowMembersListing || isExpired)),
  };
};
